import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Copy, Check, Share2, Gift, Trophy, MessageCircle } from 'lucide-react';
import DesktopHeader from '../components/DesktopHeader';
import BottomNav from '../components/BottomNav';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const Referral = () => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/referral`, authCfg());
      setData(d);
    } catch (e) {
      if (e?.response?.status === 401) nav('/login');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const link = data ? `${window.location.origin}/signup?ref=${data.code}` : '';
  const shareText = `Get ₹100 off your first Clean24 booking! Use my code ${data?.code || ''} at signup. ${link}`;

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); }
    catch { const el = document.createElement('textarea'); el.value = text; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'Clean24 Referral', text: shareText, url: link }); } catch {}
    } else {
      copy(shareText);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 md:pb-8">
      <DesktopHeader />

      <div className="max-w-[900px] mx-auto px-5 md:px-6 pt-4 md:pt-8">
        <button onClick={() => nav(-1)} className="md:hidden mb-3 inline-flex items-center gap-1 text-sm text-neutral-600"><ArrowLeft className="w-4 h-4" /> Back</button>

        {/* Hero */}
        <div data-testid="referral-hero" className="bg-white border border-neutral-200 rounded-2xl p-5 md:p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#CFFAFE] flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-[#0891B2]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Refer & earn</p>
              <h1 className="mt-1 text-2xl md:text-3xl font-extrabold text-neutral-900">Give ₹100, Get ₹100</h1>
              <p className="mt-1 text-sm text-neutral-600">Both you and your friend get ₹100 wallet credit when they complete their first booking.</p>
            </div>
          </div>
        </div>

        {/* Code card */}
        <div className="mt-4 bg-white border border-neutral-200 rounded-2xl p-5" data-testid="referral-code-card">
          <p className="text-xs uppercase tracking-wide text-neutral-500 font-bold">Your referral code</p>
          <div className="mt-2 flex items-center gap-2">
            <div data-testid="referral-code-value" className="flex-1 bg-neutral-900 text-white rounded-lg px-4 py-3 font-mono font-extrabold text-lg tracking-widest text-center">
              {data?.code || '••••-••••'}
            </div>
            <button
              data-testid="referral-copy-btn"
              onClick={() => data && copy(data.code)}
              className="px-3 py-3 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] text-white font-semibold text-sm inline-flex items-center gap-1">
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
            </button>
          </div>
          <p className="mt-3 text-xs text-neutral-500 truncate">
            Share link: <span className="font-mono text-neutral-700">{link}</span>
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="referral-share-btn" onClick={share} className="inline-flex items-center gap-1.5 bg-[#2E7D32] hover:bg-[#276D2A] text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors">
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button onClick={() => copy(shareText)} className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 hover:border-[#2E7D32] font-semibold text-sm px-4 py-2 rounded-lg transition-colors text-neutral-700">
              <MessageCircle className="w-4 h-4" /> Copy message
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <StatCard testid="referral-stat-invited" icon={Users} label="Friends invited" value={data?.invited_count ?? 0} color="#0891B2" bg="#CFFAFE" />
          <StatCard testid="referral-stat-rewarded" icon={Trophy} label="Rewarded" value={data?.rewarded_count ?? 0} color="#B45309" bg="#FEF3C7" />
        </div>

        {/* How it works */}
        <div className="mt-4 bg-white border border-neutral-200 rounded-2xl p-4" data-testid="referral-how-it-works">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-4 h-4 text-[#0891B2]" />
            <p className="font-bold text-sm">How it works</p>
          </div>
          <ol className="text-xs text-neutral-600 space-y-1.5 list-decimal pl-4">
            <li>Share your code with a friend</li>
            <li>They sign up using your code at <code className="bg-neutral-100 px-1 rounded">/signup</code></li>
            <li>Once they complete their first booking, ₹100 is credited to both wallets automatically</li>
            <li>No cap on invites — keep earning!</li>
          </ol>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, bg, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-2xl p-4 flex items-center gap-3">
    <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div>
      <p className="text-2xl font-extrabold" style={{ color }}>{value}</p>
      <p className="text-xs text-neutral-500">{label}</p>
    </div>
  </div>
);

export default Referral;
